import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import styles from './BillingCtaButton.module.css';
import { settingsThemeClass, type HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The three skins the eight billing boards draw on a 40px pill, and there is no fourth.
 *
 * - `soft` — a wash and a label. B1's "Current plan" and the Teams card's "Let's talk": a target
 *   that is either inert or secondary. Identical to `SettingsButton`'s `soft`.
 * - `key` — the plan card's one buy action: "Upgrade" on B2/B4, "Cancel plan" on B3. A dark pill
 *   with a ring and a rim-light along its bottom edge, drawn as a physical key rather than as a
 *   flat fill. See the stylesheet for why its three colours are literals.
 * - `solid` — the celebration dialog's "Let's go" / "Done". The system's one filled button, and the
 *   only skin of the three that `SettingsButton` already expresses exactly.
 */
export type BillingCtaSkin = 'soft' | 'key' | 'solid';

export interface BillingCtaButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  skin?: BillingCtaSkin;
  /** Registry name of a leading 16px glyph — `upgrade` on B2/B4, `downgrade` on B3. */
  icon?: string;
  /**
   * The Stripe round-trip has started. A spinner takes the glyph's lane and the pill stops taking
   * presses; the label stays, so a 344px CTA does not resize under the cursor.
   *
   * Not on the boards — none of the eight draws a checkout mid-flight. It is here because the plan
   * CTA it replaces carried the state, and a button that looks idle while a checkout session is
   * being created gets pressed twice.
   */
  loading?: boolean;
  /** Fills its column. The plan card's CTA and the dialog's are full-width; Teams' is not. */
  block?: boolean;
  theme?: HeaderTheme;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * The billing surface's 40px action pill.
 *
 * It exists **only** because `SettingsButton`'s tallest size is 36 and every action on the billing
 * boards is drawn at 40 — the plan card's CTA (344x40), the Teams card's button (104x40) and the
 * dialog's (308x40) — and because two of the three skins here are 14px type where `SettingsButton`
 * reserves that for `solid` alone. `soft` is a deliberate duplicate of `SettingsButton.soft` down to
 * the token; `key` is the one skin that surface has no answer for at all. The moment `SettingsButton`
 * grows a 40px size and a `key` skin, this file should delete itself and the three call sites should
 * import that instead. I could not make that change — see the report.
 */
export const BillingCtaButton = React.forwardRef<HTMLButtonElement, BillingCtaButtonProps>(
  function BillingCtaButton(
    {
      skin = 'soft',
      icon,
      loading = false,
      block = false,
      theme: themeProp,
      forceHover = false,
      className,
      children,
      type = 'button',
      disabled,
      ...rest
    },
    ref,
  ) {
  const theme = useV15Theme(themeProp);
    return (
      <button
        ref={ref}
        type={type}
        // Disabled for real rather than only painted: a second press while a checkout session is
        // being created opens a second session.
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(
          settingsThemeClass(theme),
          styles.button,
          styles[skin],
          block && styles.block,
          forceHover && styles.forceHover,
          className,
        )}
        {...rest}
      >
        {loading ? (
          <span className={styles.spinner} aria-hidden />
        ) : icon ? (
          <span className={styles.glyph}>
            {/* 16, not the label's 14: B2 and B3 draw a 16px glyph beside 14px type, because the
                arrow-in-a-circle is a shape rather than a letter and matching the x-height would
                shrink the circle below the word it leads. */}
            <DeckIcon name={icon} size={16} />
          </span>
        ) : null}
        {children}
      </button>
    );
  },
);
